//src/api/favoritosApi.ts

import { getAuthToken } from '../security/AuthLogin';

const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;
const API_URL = `${BASE_URL}/api`;

/** Tipado para los filtros opcionales */
export interface FetchFavoritosParams {
  page?: number;
  limit?: number;
  search?: string;
  fechaInicio?: string;
  fechaFin?: string;
  importeDesde?: string;
  importeHasta?: string;
  autor?: string; // Solo para admin/tecnico
  asignado?: string;
  cerrados?: string; // Filtrar por "cerrados"
  estado?: string; // Filtrar por estado de soporte
  noRespondidos?: string;
}

/** Estructura de un Favorito */
export interface Favorito {
  id_concurso: string;
  id_usuario: string;
  titulo: string;
  codigo_expediente: string;
  fecha_publicacion: string;
  entidad_compradora: string;
  pais: string;
  localidad: string;
  enlace?: string;
  importe_total?: number;
  importe_estimado?: number;
  cliente_nombre?: string;
  cliente_email?: string;
  fecha_alta_favorito?: string;
  estado_soporte?: string | null;
  estado?: string;
  fecha_fin_ofertas?: string;

  // Datos de dueño
  dueno_id?: string | null;
  dueno_nombre?: string | null;
  dueno_email?: string | null;

  // NUEVO: nuevos campos
  ultimo_emisor?: string | null;
  ultima_fecha_msg?: string | null;
  leido?: number; // 0 o 1
}

/** Respuesta al listar favoritos */
interface FetchFavoritosResponse {
  data: Favorito[];
  total: number;
  page: number;
  limit: number;
}

/** Obtiene la lista de favoritos con paginación y filtros (GET /api/favoritos). */
export const fetchFavoritos = async (
  params?: FetchFavoritosParams,
  signal?: AbortSignal
): Promise<FetchFavoritosResponse> => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = '/login';
    throw new Error('Token no encontrado. El usuario no está autenticado.');
  }

  const queryString = new URLSearchParams();
  if (params?.page) queryString.set('page', params.page.toString());
  if (params?.limit) queryString.set('limit', params.limit.toString());
  if (params?.search) queryString.set('search', params.search);
  if (params?.fechaInicio) queryString.set('fechaInicio', params.fechaInicio);
  if (params?.fechaFin) queryString.set('fechaFin', params.fechaFin);
  if (params?.importeDesde) queryString.set('importeDesde', params.importeDesde);
  if (params?.importeHasta) queryString.set('importeHasta', params.importeHasta);
  if (params?.autor) queryString.set('autor', params.autor);
  if (params?.cerrados) queryString.set('cerrados', params.cerrados);
  if (params?.estado) queryString.set("estado", params.estado);
  if (params?.asignado) queryString.set("asignado", params.asignado);
  if (params?.noRespondidos)
    queryString.set("noRespondidos", params.noRespondidos);


  const url = `${API_URL}/favoritos?${queryString.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al obtener favoritos: ${errorText}`);
  }

  return response.json();
};

/** Retorna solo los IDs de concursos marcados como favoritos. */
export const fetchFavoritosIds = async (): Promise<string[]> => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = '/login';
    throw new Error('Token no encontrado. El usuario no está autenticado.');
  }

  const response = await fetch(`${API_URL}/favoritos`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al obtener lista de favoritos: ${errorText}`);
  }

  const json = await response.json(); // { data, total, page, limit }
  const concursos = json.data || [];
  return concursos.map((c: any) => c.id_concurso);
};

/** Añadir o quitar favorito (solo para clientes). */
export const toggleFavorito = async (idConcurso: string, action: 'add' | 'remove') => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = '/login';
    throw new Error('Token no encontrado. El usuario no está autenticado.');
  }

  const response = await fetch(`${API_URL}/concursos/${idConcurso}/favorito`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al cambiar favorito: ${errorText}`);
  }

  return response.json();
};

/** Actualizar estado del favorito (solo admin/tecnico). */
export const updateEstadoFavorito = async (
  idConcurso: string,
  idUsuario: string,
  nuevoEstado: string
) => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = '/login';
    throw new Error('Token no encontrado. El usuario no está autenticado.');
  }

  const body = { estado: nuevoEstado };
  const url = `${API_URL}/favoritos/${idConcurso}/${idUsuario}/estado`;

  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al actualizar estado: ${errorText}`);
  }

  return response.json();
};

/** Obtener un favorito específico (GET /api/favoritos/detalle/:id_concurso/:id_usuario). */
export const fetchFavoritoById = async (idConcurso: string, idUsuario: string) => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = '/login';
    throw new Error('No token encontrado');
  }

  const url = `${API_URL}/favoritos/detalle/${idConcurso}/${idUsuario}`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!resp.ok) {
    const errText = await resp.text();
    throw new Error(`Error al obtener el favorito: ${errText}`);
  }

  const json = await resp.json(); // { success: true, favorito: {...} }
  return json.favorito;
};
